/**
 * RollenbeheerPage — Admin pagina voor team-user management (FASE 1).
 *
 * Functies:
 *  - Hiërarchie tree-view (admin → PM → partner → TL → agent)
 *  - Lijst van alle team-users + customers met filter
 *  - Create-modal voor nieuwe team-users (rol + parent dropdown)
 *  - Edit-modal voor rol/parent wijzigen + (de)activeren
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import {
  Users, Plus, Crown, Briefcase, ShieldCheck, UserCheck, User,
  ChevronRight, ChevronDown, RefreshCw, Search, Edit2, X, Loader2,
  Check, Power, PowerOff, GitBranch,
} from 'lucide-react';

const ROLE_LABELS = {
  admin: { label: 'Admin', icon: Crown, color: 'text-[#D4A017]', bg: 'bg-[#D4A017]/10' },
  manager: { label: 'Manager', icon: Crown, color: 'text-[#D4A017]', bg: 'bg-[#D4A017]/10' },
  projectmanager: { label: 'Projectmanager', icon: Briefcase, color: 'text-purple-400', bg: 'bg-purple-500/10' },
  partner: { label: 'Partner / Callcenter', icon: ShieldCheck, color: 'text-blue-400', bg: 'bg-blue-500/10' },
  teamleider: { label: 'Teamleider', icon: UserCheck, color: 'text-cyan-400', bg: 'bg-cyan-500/10' },
  agent: { label: 'Agent', icon: User, color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
  radar_free: { label: 'Klant (Free)', icon: User, color: 'text-white/60', bg: 'bg-white/5' },
  radar_paid: { label: 'Klant (Self-Service)', icon: User, color: 'text-white/80', bg: 'bg-white/5' },
  beheer_paid: { label: 'Klant (Managed AI)', icon: User, color: 'text-amber-300', bg: 'bg-amber-500/10' },
};

const TEAM_ROLES_CREATE = ['projectmanager', 'partner', 'teamleider', 'agent'];


export default function RollenbeheerPage() {
  const { api } = useAuth();
  const [tab, setTab] = useState('tree');
  const [tree, setTree] = useState(null);
  const [users, setUsers] = useState([]);
  const [filterRole, setFilterRole] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);

  const loadTree = useCallback(async () => {
    try {
      const r = await api('get', '/api/admin/team/hierarchy');
      setTree(r.data);
    } catch { /* ignore */ }
  }, [api]);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterRole) params.set('role', filterRole);
      if (search) params.set('search', search);
      const r = await api('get', `/api/admin/team/users?${params.toString()}`);
      setUsers(r.data.users || []);
    } catch { toast.error('Fout bij laden users'); }
    setLoading(false);
  }, [api, filterRole, search]);

  useEffect(() => { loadTree(); loadUsers(); }, [loadTree, loadUsers]);

  const reload = () => { loadTree(); loadUsers(); };

  return (
    <div className="p-6 md:p-8 max-w-7xl">
      <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-white flex items-center gap-3" style={{ fontFamily: "'Sora', sans-serif" }}>
            <Users size={26} className="text-[#D4A017]" /> Rollenbeheer
          </h1>
          <p className="text-white/60 text-sm mt-1">
            Beheer projectmanagers, partners, teamleiders en agents (FASE 1).
          </p>
        </div>
        <button
          onClick={() => setCreateOpen(true)}
          className="bg-[#D4A017] hover:bg-[#B8860B] text-[#0f172a] font-bold px-4 py-2.5 rounded-xl flex items-center gap-2"
          data-testid="open-create-modal"
        >
          <Plus size={16} /> Nieuwe team-user
        </button>
      </div>

      {/* ── Tabs ──────────────────────────────────────────── */}
      <div className="flex items-center gap-2 mb-5 border-b border-white/10">
        {[
          { id: 'tree', label: 'Hiërarchie', icon: GitBranch },
          { id: 'list', label: 'Volledige lijst', icon: Users },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`tab-${t.id}`}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 transition-colors ${
              tab === t.id ? 'border-[#D4A017] text-[#D4A017]' : 'border-transparent text-white/60 hover:text-white'
            }`}
          >
            <t.icon size={14} /> {t.label}
          </button>
        ))}
        <button
          onClick={reload}
          className="ml-auto text-white/60 hover:text-white text-sm flex items-center gap-1.5 px-3 py-1.5"
        >
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      {/* ── Tree view ─────────────────────────────────────── */}
      {tab === 'tree' && (
        <div data-testid="tree-view">
          {!tree ? (
            <div className="text-white/40 flex items-center gap-2"><Loader2 className="animate-spin" size={16} /> Laden…</div>
          ) : (
            <div className="bg-white/5 rounded-xl p-5 border border-white/10">
              {tree.roots.length === 0 ? (
                <p className="text-white/40 text-sm">Geen accounts met team-rollen gevonden.</p>
              ) : (
                tree.roots.map((node) => <TreeNode key={node.id} node={node} depth={0} onEdit={setEditUser} />)
              )}
              <p className="text-white/40 text-xs mt-4">Totaal team-users: {tree.total_users}</p>
            </div>
          )}
        </div>
      )}

      {/* ── List view ─────────────────────────────────────── */}
      {tab === 'list' && (
        <div data-testid="list-view">
          <div className="flex items-center gap-3 mb-4 flex-wrap">
            <select
              value={filterRole}
              onChange={(e) => setFilterRole(e.target.value)}
              className="bg-white/5 border border-white/10 text-white rounded-lg px-3 py-2 text-sm"
              data-testid="filter-role"
            >
              <option value="">Alle rollen</option>
              <option value="projectmanager">Projectmanager</option>
              <option value="partner">Partner / Callcenter</option>
              <option value="teamleider">Teamleider</option>
              <option value="agent">Agent</option>
              <option value="radar_free">Klant (Free)</option>
              <option value="radar_paid">Klant (Self-Service)</option>
              <option value="beheer_paid">Klant (Managed AI)</option>
            </select>
            <div className="flex items-center gap-2 flex-1 max-w-md bg-white/5 border border-white/10 rounded-lg px-3 py-2">
              <Search size={14} className="text-white/40" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Zoek op naam of e-mail…"
                className="bg-transparent text-white text-sm outline-none flex-1"
                data-testid="search-input"
              />
            </div>
          </div>
          {loading ? (
            <div className="text-white/40 flex items-center gap-2"><Loader2 className="animate-spin" size={16} /> Laden…</div>
          ) : (
            <div className="bg-white/5 rounded-xl border border-white/10 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-white/5">
                  <tr className="text-left text-white/50 text-xs uppercase tracking-wider">
                    <th className="px-4 py-3">Naam</th>
                    <th className="px-4 py-3">E-mail</th>
                    <th className="px-4 py-3">Rol</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {users.map((u) => {
                    const r = ROLE_LABELS[u.role] || { label: u.role, icon: User, color: 'text-white/60', bg: 'bg-white/5' };
                    const RIcon = r.icon;
                    return (
                      <tr key={u.id} data-testid={`user-row-${u.id}`} className="hover:bg-white/5">
                        <td className="px-4 py-3 text-white">{u.name || '—'}</td>
                        <td className="px-4 py-3 text-white/70 font-mono text-xs">{u.email}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs ${r.bg} ${r.color}`}>
                            <RIcon size={10} /> {r.label}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          {u.active === false ? (
                            <span className="text-red-400 text-xs flex items-center gap-1"><PowerOff size={10} /> Inactief</span>
                          ) : (
                            <span className="text-emerald-400 text-xs flex items-center gap-1"><Power size={10} /> Actief</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <button
                            onClick={() => setEditUser(u)}
                            className="text-[#D4A017] hover:text-[#B8860B] text-xs font-semibold inline-flex items-center gap-1"
                            data-testid={`edit-btn-${u.id}`}
                          >
                            <Edit2 size={12} /> Beheer
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                  {users.length === 0 && (
                    <tr><td colSpan="5" className="px-4 py-8 text-center text-white/40">Geen gebruikers gevonden</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── Create modal ──────────────────────────────────── */}
      {createOpen && (
        <CreateUserModal
          api={api}
          onClose={() => setCreateOpen(false)}
          onCreated={() => { setCreateOpen(false); reload(); }}
        />
      )}

      {/* ── Edit modal ────────────────────────────────────── */}
      {editUser && (
        <EditUserModal
          api={api}
          user={editUser}
          onClose={() => setEditUser(null)}
          onSaved={() => { setEditUser(null); reload(); }}
        />
      )}
    </div>
  );
}


function TreeNode({ node, depth, onEdit }) {
  const [open, setOpen] = useState(true);
  const r = ROLE_LABELS[node.role] || { label: node.role, icon: User, color: 'text-white/60', bg: 'bg-white/5' };
  const RIcon = r.icon;
  const hasChildren = (node.children || []).length > 0;

  return (
    <div className="my-1" data-testid={`tree-node-${node.id}`}>
      <div className="flex items-center gap-2 py-1.5" style={{ paddingLeft: `${depth * 24}px` }}>
        {hasChildren ? (
          <button onClick={() => setOpen(!open)} className="text-white/40 hover:text-white">
            {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          </button>
        ) : (
          <span className="w-3.5" />
        )}
        <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs ${r.bg} ${r.color}`}>
          <RIcon size={10} /> {r.label}
        </span>
        <span className="text-white text-sm">{node.name || node.email}</span>
        <span className="text-white/40 text-xs font-mono">{node.email}</span>
        {node.active === false && (
          <span className="text-red-400 text-xs ml-2">Inactief</span>
        )}
        <button
          onClick={() => onEdit(node)}
          className="ml-auto text-white/40 hover:text-[#D4A017] text-xs"
          data-testid={`tree-edit-${node.id}`}
        >
          <Edit2 size={12} />
        </button>
      </div>
      {open && hasChildren && (
        <div>
          {node.children.map((c) => <TreeNode key={c.id} node={c} depth={depth + 1} onEdit={onEdit} />)}
        </div>
      )}
    </div>
  );
}


function CreateUserModal({ api, onClose, onCreated }) {
  const [role, setRole] = useState('projectmanager');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [parentId, setParentId] = useState('');
  const [orgName, setOrgName] = useState('');
  const [parentOptions, setParentOptions] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      if (role === 'projectmanager') {
        setParentOptions([]);
        setParentId('');
        return;
      }
      try {
        const r = await api('get', `/api/admin/team/parent-options?role=${role}`);
        setParentOptions(r.data.options || []);
        if ((r.data.options || []).length > 0) setParentId(r.data.options[0].id);
      } catch { /* ignore */ }
    })();
  }, [api, role]);

  const submit = async (e) => {
    e.preventDefault();
    if (!name || !email) return;
    setSaving(true);
    try {
      const r = await api('post', '/api/admin/team/users', {
        email, name, role, phone,
        parent_user_id: parentId || null,
        organization_name: role === 'partner' ? orgName : null,
      });
      toast.success(`Aangemaakt — wachtwoord: ${r.data.temp_password || 'door admin gezet'}`);
      onCreated();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Aanmaken mislukt');
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#0f172a] border border-white/10 rounded-2xl p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="create-modal">
        <div className="flex items-start justify-between mb-4">
          <h2 className="text-xl font-bold text-white" style={{ fontFamily: "'Sora', sans-serif" }}>Nieuwe team-user</h2>
          <button onClick={onClose} className="text-white/40 hover:text-white"><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="text-xs uppercase tracking-wide text-white/50 font-semibold block mb-1">Rol</label>
            <select value={role} onChange={(e) => setRole(e.target.value)} className="w-full bg-white/5 border border-white/10 text-white rounded-lg px-3 py-2" data-testid="role-select">
              {TEAM_ROLES_CREATE.map((r) => (
                <option key={r} value={r}>{ROLE_LABELS[r]?.label || r}</option>
              ))}
            </select>
          </div>
          {role !== 'projectmanager' && (
            <div>
              <label className="text-xs uppercase tracking-wide text-white/50 font-semibold block mb-1">Parent (rapporteert aan)</label>
              <select value={parentId} onChange={(e) => setParentId(e.target.value)} className="w-full bg-white/5 border border-white/10 text-white rounded-lg px-3 py-2" data-testid="parent-select">
                <option value="">— geen parent —</option>
                {parentOptions.map((o) => (
                  <option key={o.id} value={o.id}>{ROLE_LABELS[o.role]?.label}: {o.name} ({o.email})</option>
                ))}
              </select>
            </div>
          )}
          {role === 'partner' && (
            <div>
              <label className="text-xs uppercase tracking-wide text-white/50 font-semibold block mb-1">Organisatie naam</label>
              <input value={orgName} onChange={(e) => setOrgName(e.target.value)} className="w-full bg-white/5 border border-white/10 text-white rounded-lg px-3 py-2" placeholder="Bv. Sales Partners BV" />
            </div>
          )}
          <div>
            <label className="text-xs uppercase tracking-wide text-white/50 font-semibold block mb-1">Naam *</label>
            <input value={name} onChange={(e) => setName(e.target.value)} required className="w-full bg-white/5 border border-white/10 text-white rounded-lg px-3 py-2" data-testid="name-input" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wide text-white/50 font-semibold block mb-1">E-mail *</label>
            <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" required className="w-full bg-white/5 border border-white/10 text-white rounded-lg px-3 py-2" data-testid="email-input" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wide text-white/50 font-semibold block mb-1">Telefoon (optioneel)</label>
            <input value={phone} onChange={(e) => setPhone(e.target.value)} className="w-full bg-white/5 border border-white/10 text-white rounded-lg px-3 py-2" />
          </div>
          <button type="submit" disabled={saving} className="w-full bg-[#D4A017] hover:bg-[#B8860B] disabled:opacity-50 text-[#0f172a] font-bold py-2.5 rounded-xl mt-3 flex items-center justify-center gap-2" data-testid="submit-btn">
            {saving ? <><Loader2 size={14} className="animate-spin" /> Aanmaken…</> : <><Check size={14} /> Account aanmaken</>}
          </button>
          <p className="text-xs text-white/40 text-center">Een tijdelijk wachtwoord wordt gegenereerd. Deel via veilig kanaal.</p>
        </form>
      </div>
    </div>
  );
}


function EditUserModal({ api, user, onClose, onSaved }) {
  const [name, setName] = useState(user.name || '');
  const [phone, setPhone] = useState(user.phone || '');
  const [notes, setNotes] = useState(user.notes || '');
  const [active, setActive] = useState(user.active !== false);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api('patch', `/api/admin/team/users/${user.id}`, { name, phone, notes, active });
      toast.success('Opgeslagen');
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Opslaan mislukt');
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#0f172a] border border-white/10 rounded-2xl p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="edit-modal">
        <div className="flex items-start justify-between mb-4">
          <h2 className="text-xl font-bold text-white" style={{ fontFamily: "'Sora', sans-serif" }}>{user.name || user.email}</h2>
          <button onClick={onClose} className="text-white/40 hover:text-white"><X size={18} /></button>
        </div>
        <p className="text-xs text-white/40 mb-4">Rol: <span className="text-white font-semibold">{ROLE_LABELS[user.role]?.label || user.role}</span></p>
        <div className="space-y-3">
          <div>
            <label className="text-xs uppercase tracking-wide text-white/50 font-semibold block mb-1">Naam</label>
            <input value={name} onChange={(e) => setName(e.target.value)} className="w-full bg-white/5 border border-white/10 text-white rounded-lg px-3 py-2" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wide text-white/50 font-semibold block mb-1">Telefoon</label>
            <input value={phone} onChange={(e) => setPhone(e.target.value)} className="w-full bg-white/5 border border-white/10 text-white rounded-lg px-3 py-2" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wide text-white/50 font-semibold block mb-1">Notities</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} className="w-full bg-white/5 border border-white/10 text-white rounded-lg px-3 py-2" />
          </div>
          <label className="flex items-center gap-2 text-white text-sm cursor-pointer">
            <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} className="w-4 h-4" data-testid="active-checkbox" />
            Account actief (uncheck om te deactiveren)
          </label>
          <button onClick={save} disabled={saving} className="w-full bg-[#D4A017] hover:bg-[#B8860B] disabled:opacity-50 text-[#0f172a] font-bold py-2.5 rounded-xl mt-3 flex items-center justify-center gap-2" data-testid="save-btn">
            {saving ? <><Loader2 size={14} className="animate-spin" /> Opslaan…</> : <><Check size={14} /> Wijzigingen opslaan</>}
          </button>
        </div>
      </div>
    </div>
  );
}
